import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useCampAccommodationStore } from '../../stores/campAccommodationStore';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddCampTableModal = ({ isOpen, onClose }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectedCamp, setSelectedCamp] = useState('');
  const [tableName, setTableName] = useState('');
  const { createTable } = useCampAccommodationStore();

  const handleDownloadTemplate = () => {
    // Create template data with the new format
    const template = [
      {
        'رقم الخيمة': 1,
        'رقم الصالة': 1,
        'عدد الاسرة': 1,
        'نوع الاسرة': 'male'
      },
      {
        'رقم الخيمة': 1,
        'رقم الصالة': 2,
        'عدد الاسرة': 2,
        'نوع الاسرة': 'female'
      }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    // Save file
    XLSX.writeFile(wb, 'camp-accommodation-template.xlsx');
  };

  const handleSubmit = async () => {
    if (!file || !selectedCamp || !tableName) {
      toast.error('جميع الحقول مطلوبة');
      return;
    }

    setIsLoading(true);
    try {
      await createTable(selectedCamp, selectedCamp, tableName, file);
      toast.success('تم إنشاء جدول التسكين بنجاح');
      onClose();
    } catch (error: any) {
      console.error('Error creating table:', error);
      toast.error(error.message || 'فشل في إنشاء جدول التسكين');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="إضافة جدول تسكين مخيم">
      <div className="space-y-6">
        {/* Camp Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            المخيم
            <span className="text-red-500 mr-1">*</span>
          </label>
          <select
            value={selectedCamp}
            onChange={(e) => setSelectedCamp(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">اختر المخيم</option>
            <option value="Majar al kabas">مجر الكبش</option>
            <option value="al measuem">المعيصم</option>
          </select>
        </div>

        {/* Table Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            اسم الجدول
            <span className="text-red-500 mr-1">*</span>
          </label>
          <input
            type="text"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>تحميل نموذج Excel</span>
        </button>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ملف Excel
            <span className="text-red-500 mr-1">*</span>
          </label>
          <FileUploadZone
            file={file}
            onFileSelect={setFile}
            onFileRemove={() => {
              setFile(null);
              setValidationErrors([]);
            }}
          />
        </div>

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !file || !selectedCamp || !tableName}
            className={`px-6 py-2 rounded-lg text-white
              ${!file || isLoading || !selectedCamp || !tableName
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isLoading ? 'جاري الإنشاء...' : 'إنشاء'}
          </button>
        </div>
      </div>
    </Modal>
  );
};